import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { FILE_ACCESS_URL } from '../../../../configs/constants';
import { getItem } from '../../../../utils/localStorageUtils';

const ProjectDetail = () => {
  const { id } = useParams(); // Get the project ID from the URL parameters
  const [project, setProject] = useState(null); // State to store the project data
  const [loading, setLoading] = useState(true); // State to manage loading state
  const [error, setError] = useState(null); // State to manage error state

  useEffect(() => {
    const fetchProjectData = async () => {
      try {
        setLoading(true);
        setError(null);
        // Check localStorage first
        const cachedProjects = getItem('projects_data');
        if (cachedProjects) {
          // Find project in cached data
          const foundProject = cachedProjects.find(p => p._id === id);
          if (foundProject) {
            console.log("Using cached project data");
            setProject(foundProject);
            setLoading(false);
            return;
          }
        }
        // For now, we'll show no data found if not in cache
        console.log("Project not found in cache");
        setError("Project not found");
        setLoading(false);

      } catch (err) {
        console.error("Error fetching project:", err);
        setError("Something went wrong while loading the project");
        setLoading(false);
      }
    };

    if (id) {
      fetchProjectData();
    }
  }, [id]); // Re-run the effect if the ID changes

  // Modern Loading Component
  const LoadingComponent = () => (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-blue-50">
      <div className="text-center">
        {/* Animated Spinner */}
        <div className="relative mb-8">
          <div className="w-20 h-20 border-4 border-gray-200 border-t-sky-400 rounded-full animate-spin mx-auto"></div>
          <div className="absolute inset-0 w-20 h-20 border-4 border-transparent border-r-sky-400 rounded-full animate-spin mx-auto" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
        </div>

        {/* Loading Text with Animation */}
        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-gray-800 animate-pulse">Loading Project</h2>
          <p className="text-gray-600 animate-bounce">Fetching project details...</p>
        </div>

        {/* Progress Dots */}
        <div className="flex justify-center space-x-2 mt-6">
          <div className="w-2 h-2 bg-sky-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
          <div className="w-2 h-2 bg-sky-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
          <div className="w-2 h-2 bg-sky-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
        </div>
      </div>
    </div>
  );

  // Modern No Data Component
  const NoDataComponent = () => (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-blue-50">
      <div className="text-center max-w-md mx-auto px-4">
        {/* Animated Icon */}
        <div className="relative mb-8">
          <div className="w-32 h-32 mx-auto bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center animate-pulse">
            <svg className="w-16 h-16 text-gray-500 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.009-5.824-2.709M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          {/* Floating Elements */}
          <div className="absolute -top-2 -right-2 w-4 h-4 bg-sky-400 rounded-full animate-ping"></div>
          <div className="absolute -bottom-2 -left-2 w-3 h-3 bg-sky-400 rounded-full animate-ping" style={{ animationDelay: '1s' }}></div>
        </div>

        {/* No Data Text */}
        <div className="space-y-4">
          <h2 className="text-3xl font-bold text-gray-800">Project Not Found</h2>
          <p className="text-gray-600 leading-relaxed">
            {error || "The project you're looking for doesn't exist or has been removed."}
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-6">
            <Link
              to="/project"
              className="px-6 py-3 bg-sky-500 text-white font-semibold rounded-lg hover:bg-sky-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <span className="flex items-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>Back to Projects</span>
              </span>
            </Link>

            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-gray-500 text-white font-semibold rounded-lg hover:bg-gray-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <span className="flex items-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Try Again</span>
              </span>
            </button>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="mt-8 flex justify-center space-x-4">
          <div className="w-2 h-2 bg-gray-300 rounded-full animate-pulse"></div>
          <div className="w-2 h-2 bg-gray-300 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
          <div className="w-2 h-2 bg-gray-300 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <LoadingComponent />;
  }

  if (!project || error) {
    return <NoDataComponent />;
  }

  // Process tags - handle both string and array formats, split by comma, remove quotes
  const processTags = (tags) => {
    if (!tags) return [];
    let allTags = [];
    
    // Helper function to remove quotes and trim
    const cleanTag = (tag) => {
      if (typeof tag !== 'string') return tag;
      return tag.trim().replace(/^["']|["']$/g, '').trim();
    };
    
    if (typeof tags === 'string') {
      // Split by comma and process each part
      allTags = tags.split(',').map(tag => cleanTag(tag)).filter(tag => tag.length > 0);
    } else if (Array.isArray(tags)) {
      // For arrays, split each item if it contains commas
      tags.forEach(tag => {
        if (typeof tag === 'string') {
          // Split each array item by comma if it contains commas
          const splitTags = tag.split(',').map(t => cleanTag(t)).filter(t => t.length > 0);
          allTags = [...allTags, ...splitTags];
        } else if (tag) {
          allTags.push(cleanTag(tag));
        }
      });
    }
    
    // Remove duplicates and return
    return [...new Set(allTags)];
  };

  const tagsArray = processTags(project.tags);

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-50 py-8">
      <div className="container mx-auto px-4">
        {/* Back to Projects Link */}
        <Link
          to="/project"
          className="inline-flex items-center gap-2 text-gray-700 hover:text-sky-600 transition-colors duration-300 mb-8 group"
        >
          <FaArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-300" />
          <span className="font-medium">Back to Projects</span>
        </Link>

        {/* Project Header */}
        <div className="text-center mb-12">
          {project.logo && (
            <div className="mb-6 rounded-2xl shadow-lg w-32 h-32 mx-auto p-4">
              <img
                src={`${FILE_ACCESS_URL}${project.logo}`}
                className="w-32 h-32 mx-auto object-contain"
                alt={`${project.name || project.title} Logo`}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}

          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            {project.name || project.title || 'Project Name'}
          </h1>

          {project.desc && (
            <p className="text-lg text-gray-600 md:px-20 mx-auto leading-relaxed">
              {project.desc}
            </p>
          )}
        </div>

        {/* Project Images */}
        {(project.images || project.photos) && (project.images?.length > 0 || project.photos?.length > 0) && (
          <div className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6 text-center">Project Screenshots</h2>
            <div className="flex flex-wrap justify-center gap-6">
              {(project.photos || project.images).map((image, index) => (
                <div key={index} className="group w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] max-w-sm">
                  <img
                    src={`${FILE_ACCESS_URL}${image}`}
                    className="w-full h-auto rounded-2xl shadow-lg transition-transform duration-300 group-hover:scale-105"
                    alt={`Screenshot ${index + 1}`}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tech Stack Section */}
        {tagsArray.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-800 mb-8 text-center">Technologies Used</h2>
            <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
              {tagsArray.map((tech, index) => {
                const activeColor = project.activeColor || '#0ea5e9';
                return (
                  <span
                    key={index}
                    className="group relative px-5 py-1 bg-white font-medium rounded-full "
                    style={{
                      border: `1px solid ${activeColor}40`,
                      backgroundColor: `${activeColor}08`,
                    }}
                  >
                    <span 
                      className="relative z-10"
                      style={{
                        color: activeColor,
                      }}
                    >
                      {tech}
                    </span>
                    <span 
                      className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      style={{
                        background: `linear-gradient(135deg, ${activeColor}15, ${activeColor}25)`,
                      }}
                    ></span>
                  </span>
                )
              })}
            </div>
          </div>
        )}

        {/* Project Links */}
        <div className="text-center">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {project.web && (
              <a
                href={project.web}
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 bg-sky-500 text-white font-semibold rounded-lg hover:bg-sky-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <span className="flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  <span>View Website</span>
                </span>
              </a>
            )}

            {project.app && (
              <a
                href={project.app}
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 bg-emerald-500 text-white font-semibold rounded-lg hover:bg-emerald-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <span className="flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <span>View App</span>
                </span>
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
